import React from 'react';
import { Briefcase, MapPin, Clock, Sparkles, ExternalLink, MessageCircle } from 'lucide-react';
import { CandidateForRecommendation } from '../services/recommendationsApi';

interface CandidateCardProps {
    candidate: CandidateForRecommendation;
    onGetRecommendations: (candidate: CandidateForRecommendation) => void;
    isLoading?: boolean;
}

const CandidateCard: React.FC<CandidateCardProps> = ({
    candidate,
    onGetRecommendations,
    isLoading = false
}) => {
    const getInitials = (firstName?: string, lastName?: string) => {
        if (firstName && lastName) {
            return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
        }
        if (firstName) {
            return firstName.charAt(0).toUpperCase();
        }
        return '??';
    };

    const getExperienceBadge = (years?: number) => {
        if (!years) return null;
        if (years >= 10) return '10+ yrs exp';
        if (years >= 5) return '5+ yrs exp';
        if (years >= 3) return '3+ yrs exp';
        if (years >= 1) return '1+ yr exp';
        return 'Entry level';
    };

    const formatLastContacted = (dateString?: string) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - date.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        return `${Math.floor(diffDays / 30)} months ago`;
    };

    const displaySkills = candidate.skills?.slice(0, 4) || [];
    const remainingSkillsCount = (candidate.skills?.length || 0) - 4;
    const fullName = `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() || 'Unknown';
    const experienceBadge = getExperienceBadge(candidate.yearsOfExperience);
    const lastContacted = formatLastContacted(candidate.lastContactedAt);

    return (
        <div className="bg-white rounded-xl p-5 border border-[#EDEDED] hover:shadow-md hover:border-[#015FA6]/20 transition-all duration-200 group">
            <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-lg bg-gradient-to-br from-[#015FA6] to-[#0077cc] flex-shrink-0">
                    {getInitials(candidate.firstName, candidate.lastName)}
                </div>

                {/* Main Content */}
                <div className="flex-1 min-w-0">
                    {/* Name and Email Row */}
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <h3 className="text-base font-semibold text-[#0B111E] truncate">
                                {fullName}
                            </h3>
                            <p className="text-sm text-[#757575] truncate">
                                {candidate.email}
                            </p>
                        </div>

                        {/* Last Contacted Badge */}
                        {lastContacted && (
                            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#F1F3F4] text-xs text-[#515151] flex-shrink-0">
                                <Clock size={12} />
                                {lastContacted}
                            </div>
                        )}
                    </div>

                    {/* Info Row */}
                    <div className="flex flex-wrap items-center gap-3 mt-3">
                        {experienceBadge && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-[#015FA6] border border-blue-100">
                                <Briefcase size={12} />
                                {experienceBadge}
                            </span>
                        )}

                        {candidate.currentTitle && (
                            <span className="text-xs text-[#515151]">
                                {candidate.currentTitle}
                            </span>
                        )}

                        {candidate.location && (
                            <span className="inline-flex items-center gap-1 text-xs text-[#757575]">
                                <MapPin size={12} />
                                {candidate.location}
                            </span>
                        )}

                        {candidate.source === 'imported' && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                Imported
                            </span>
                        )}

                        {candidate.linkedinUrl && (
                            <a
                                href={candidate.linkedinUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={e => e.stopPropagation()}
                                className="inline-flex items-center gap-1 text-xs text-[#015FA6] hover:underline"
                            >
                                <ExternalLink size={12} />
                                LinkedIn
                            </a>
                        )}
                    </div>

                    {/* Skills Row */}
                    {displaySkills.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5 mt-3">
                            {displaySkills.map((skill, index) => (
                                <span
                                    key={index}
                                    className="px-2 py-0.5 rounded text-xs bg-[#F1F3F4] text-[#515151]"
                                >
                                    {skill}
                                </span>
                            ))}
                            {remainingSkillsCount > 0 && (
                                <span className="px-2 py-0.5 rounded text-xs bg-[#F1F3F4] text-[#757575]">
                                    +{remainingSkillsCount} more
                                </span>
                            )}
                        </div>
                    )}

                    {/* Last Message Preview */}
                    {candidate.lastMessage && (
                        <div className="mt-3 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-[#F8F9FA] border border-[#EDEDED]">
                            <MessageCircle size={14} className="text-[#015FA6] mt-0.5 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                                <p className="text-xs text-[#515151] line-clamp-2">
                                    {candidate.lastMessage.content}
                                </p>
                                <p className="text-[10px] text-[#A5A5A5] mt-1">
                                    Sent {formatLastContacted(candidate.lastMessage.timestamp)}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Action Button */}
                <button
                    onClick={() => onGetRecommendations(candidate)}
                    disabled={isLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-[#015FA6] text-white rounded-lg text-sm font-medium hover:bg-[#014a85] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0 group-hover:shadow-sm"
                >
                    {isLoading ? (
                        <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Loading...
                        </>
                    ) : (
                        <>
                            <Sparkles size={16} />
                            Get Recommendations
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default CandidateCard;
